import React, { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger, MotionPathPlugin } from "gsap/all";
import InfoBox from "./InfoBox";
import { processData } from "../utils/processData";
// import RichText from "./RichText";

gsap.registerPlugin(MotionPathPlugin, ScrollTrigger);

const TractorAnimation = () => {
  useEffect(() => {
    const motionPathElem = document.querySelector("#motionPath");
    if (!motionPathElem) return; // Prevent error if not found

    gsap.set("#linesvg", { opacity: 1 });
    gsap.set("#motionSVG", { scale: 0.7, autoAlpha: 1 });
    gsap.set("#tractor", { transformOrigin: "50% 50%" });

    let rotateTo = gsap.quickTo("#tractor", "rotation");
    let prevDirection = 0;

    gsap.to("#motionSVG", {
      scrollTrigger: {
        trigger: "#motionPath",
        start: "top center",
        end: () => "+=" + motionPathElem.getBoundingClientRect().height,
        scrub: 0.5,
        onUpdate: (self) => {
          if (prevDirection !== self.direction) {
            rotateTo(self.direction === 1 ? 0 : -180);
            prevDirection = self.direction;
          }
        },
      },
      ease: pathEase("#motionPath"),
      immediateRender: true,
      motionPath: {
        path: "#motionPath",
        align: "#motionPath",
        alignOrigin: [0.5, 0.5],
        autoRotate: 180,
      },
    });
  }, []);

  // Helper function for custom ease
  const pathEase = (path, config = {}) => {
    let axis = config.axis || "y",
      precision = config.precision || 1,
      rawPath = MotionPathPlugin.cacheRawPathMeasurements(
        MotionPathPlugin.getRawPath(gsap.utils.toArray(path)[0]),
        Math.round(precision * 12)
      ),
      useX = axis === "x",
      start = rawPath[0][useX ? 0 : 1],
      end =
        rawPath[rawPath.length - 1][
          rawPath[rawPath.length - 1].length - (useX ? 2 : 1)
        ],
      range = end - start,
      l = Math.round(precision * 200),
      inc = 1 / l,
      positions = [0],
      a = [0],
      minIndex = 0,
      smooth = [0],
      minChange = (1 / l) * 0.6,
      smoothRange = config.smooth === true ? 7 : Math.round(config.smooth) || 0,
      fullSmoothRange = smoothRange * 2,
      getClosest = (p) => {
        while (positions[minIndex] <= p && minIndex++ < l) {}
        a.push(
          ((p - positions[minIndex - 1]) /
            (positions[minIndex] - positions[minIndex - 1])) *
            inc +
            minIndex * inc
        );
        smoothRange &&
          a.length > smoothRange &&
          a[a.length - 1] - a[a.length - 2] < minChange &&
          smooth.push(a.length - smoothRange);
      },
      i = 1;
    for (; i < l; i++) {
      positions[i] =
        (MotionPathPlugin.getPositionOnPath(rawPath, i / l)[axis] - start) /
        range;
    }
    positions[l] = 1;
    for (i = 0; i < l; i++) {
      getClosest(i / l);
    }
    a.push(1); // must end at 1.
    if (smoothRange) {
      smooth.push(l - fullSmoothRange + 1);
      smooth.forEach((i) => {
        let start = a[i],
          j = Math.min(i + fullSmoothRange, l),
          inc = (a[j] - start) / (j - i),
          c = 1;
        i++;
        for (; i < j; i++) {
          a[i] = start + inc * c++;
        }
      });
    }
    l = a.length - 1;
    return (p) => {
      let i = p * l,
        s = a[i | 0];
      return i ? s + (a[Math.ceil(i)] - s) * (i % 1) : 0;
    };
  };

  return (
    <div className="relative bg-black -mb-40 md:-mb-96 px-3">
      <h2 className="bg-black text-4xl font-bold text-center text-white pt-12 lg:-mb-96 md:-mb-20">
        Onze werkwijze
      </h2>
      <svg
        id="linesvg"
        opacity="0"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        viewBox="-15 0 890 3005"
        // viewBox="0 0 869 3002"
        xmlSpace="preserve"
      >
        <style>
          {`
            .road {
              fill: none;
              stroke: gray;
              stroke-width: 60;
              stroke-linecap: round;
              stroke-linejoin: round;
              stroke-miterlimit: 10;
              filter: drop-shadow(0 0 4px white);
            }
            .road-center-line {
              fill: none;
              stroke: white;
              stroke-width: 2;
              stroke-linecap: round;
              stroke-linejoin: round;
              stroke-miterlimit: 10;
              stroke-dasharray: 10 10;
            }
          `}
        </style>
        <path
          id="motionPath"
          className="road"
          d="M155.395,383.31 C152.773,390.548 92.401,646.162 250.215,727.041 453.479,831.213 835.629,715.412 832.33,924.268 830.006,1071.385 20.339,1040.965 22.58,1206.204 24.517,1348.994 835.125,1320.378 832.275,1445.504 827.175,1669.362 57.235,1623.348 56.673,1760.63 55.674,2004.272 837.157,1936.609 837.205,2053.845 837.283,2246.807 137.92199,2252.96102 137.92199,2252.96102"
        />

        <path
          className="road-center-line"
          d="M155.395,383.31 C152.773,390.548 92.401,646.162 250.215,727.041 453.479,831.213 835.629,715.412 832.33,924.268 830.006,1071.385 20.339,1040.965 22.58,1206.204 24.517,1348.994 835.125,1320.378 832.275,1445.504 827.175,1669.362 57.235,1623.348 56.673,1760.63 55.674,2004.272 837.157,1936.609 837.205,2053.845 837.283,2246.807 137.92199,2252.96102 137.92199,2252.96102"
        />
        <g id="motionSVG">
          <g id="tractor">
            <image
              href="/assets/icons/animation_car.png"
              x="-50"
              y="-50"
              width="100"
              height="100"
              preserveAspectRatio="xMidYMid meet"
            />
          </g>
        </g>
      </svg>
      <InfoBox data={processData} />
    </div>
  );
};

export default TractorAnimation;
